import React, { FC, useRef, ChangeEvent } from 'react'
import { Grid, IconButton, InputAdornment, MenuItem, SelectChangeEvent } from '@mui/material'
import { Close } from '@mui/icons-material'
import * as Yup from 'yup'
import { useFormik, FormikProvider, Form } from 'formik'
import { AutocompleteTextField, FormDropdown, NumberTextField, TextField } from 'src/components/ui'
import { useQueryParams } from 'src/hooks/ui'
import { SearchParamsKeys } from 'src/enums'
import { Contractor, AppVersionItem } from 'src/api'
import { WEB_USERS_LABEL } from 'src/config'

const validationSchema = Yup.object().shape({
  [SearchParamsKeys.Contractor]: Yup.string().nullable(),
  [SearchParamsKeys.LastActive]: Yup.number().integer().min(0).max(366).nullable(),
  [SearchParamsKeys.AppVersion]: Yup.array().of(Yup.string())
})

const getAppVersionLabel = (version: string, count?: number): string => {
  const label = version === '' ? WEB_USERS_LABEL : version
  return count !== undefined ? `${label} (${count} users)` : label
}

interface UserListFiltersProps {
  currentContractor?: Contractor | null
  contractors?: Contractor[]
  contractorsLoading?: boolean
  findContractorsByName?: (name: string) => void
  appVersions?: AppVersionItem[]
}

export const UserListFilters: FC<UserListFiltersProps> = ({
  contractors = [],
  contractorsLoading = false,
  findContractorsByName = () => {},
  currentContractor = null,
  appVersions = []
}) => {
  const resetTimer = useRef<NodeJS.Timeout | null>(null)
  const [queryParams, setQueryParams] = useQueryParams()

  const formik = useFormik({
    validationSchema,
    initialValues: {
      [SearchParamsKeys.Contractor]: queryParams[SearchParamsKeys.Contractor] || null,
      [SearchParamsKeys.LastActive]: queryParams[SearchParamsKeys.LastActive] || null,
      [SearchParamsKeys.AppVersion]: queryParams[SearchParamsKeys.AppVersion]?.split(',') || []
    },
    onSubmit: async (formState) => {
      const lastActive = formState[SearchParamsKeys.LastActive] || null
      const contractor = formState[SearchParamsKeys.Contractor] || null
      const appVersion: string[] = formState[SearchParamsKeys.AppVersion] || []
      await setQueryParams(
        {
          ...(lastActive ? { [SearchParamsKeys.LastActive]: `${lastActive}` } : {}),
          ...(contractor ? { [SearchParamsKeys.Contractor]: `${contractor}` } : {}),
          ...(appVersion.length > 0 ? { [SearchParamsKeys.AppVersion]: appVersion.join(',') } : {})
        },
        [SearchParamsKeys.LastActive, SearchParamsKeys.Contractor, SearchParamsKeys.AppVersion]
      )
    }
  })

  const { submitForm, values, setFieldValue } = formik

  const lastActive = values[SearchParamsKeys.LastActive] || ''

  const onReset = (key: string) => () => {
    setFieldValue(key, null)
    submitForm()
  }

  const handleAppVersionChange = (event: SelectChangeEvent<string[]>) => {
    const {
      target: { value }
    } = event
    setFieldValue(SearchParamsKeys.AppVersion, typeof value === 'string' ? value.split(',') : value)
  }

  const handleAppVersionClose = () => submitForm()

  const onActiveDaysChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    if (resetTimer?.current) clearTimeout(resetTimer.current)
    const cb = () => {
      resetTimer.current = null
      setFieldValue(SearchParamsKeys.LastActive, value)
      submitForm()
    }
    resetTimer.current = setTimeout(cb, 200)
  }

  return (
    <FormikProvider value={formik}>
      <Form>
        <Grid container alignItems="flex-start" spacing={4}>
          <Grid item>
            <AutocompleteTextField
              label="Organization"
              name={SearchParamsKeys.Contractor}
              labelField="name"
              valueField="id"
              options={contractors}
              onInputChange={findContractorsByName}
              isLoading={contractorsLoading}
              onChange={submitForm}
              initialValue={currentContractor}
            />
          </Grid>
          <Grid item>
            <TextField
              name={SearchParamsKeys.LastActive}
              label="Last active, days"
              onChange={onActiveDaysChange}
              TextFieldProps={{
                InputProps: {
                  inputComponent: NumberTextField as any,
                  endAdornment: (
                    <InputAdornment position="end" sx={{ width: 30 }}>
                      {lastActive.length > 0 && (
                        <IconButton size="small" onClick={onReset(SearchParamsKeys.LastActive)}>
                          <Close fontSize="small" />
                        </IconButton>
                      )}
                    </InputAdornment>
                  ),
                  placeholder: 'Days count'
                }
              }}
            />
          </Grid>
          <Grid item width={{ xs: '100%', sm: '300px' }}>
            <FormDropdown
              name={SearchParamsKeys.AppVersion}
              label="App version"
              list={appVersions}
              menuItemFactory={(item) => (
                <MenuItem key={item.version ?? ''} value={item.version ?? ''}>
                  {getAppVersionLabel(item.version ?? '', item.count)}
                </MenuItem>
              )}
              onChange={handleAppVersionChange}
              onClose={handleAppVersionClose}
              multiple
              required
            />
          </Grid>
        </Grid>
      </Form>
    </FormikProvider>
  )
}
